/**
 * EsignConsent Component
 *
 * A reusable component for collecting electronic signature consent per ESIGN Act requirements.
 * Completely decoupled from signature capture, allowing flexible composition.
 *
 * Features:
 * - Configurable consent items
 * - Built-in ESIGN Act compliance options
 * - Tracks consent state and timestamps
 * - Generic audit metadata support
 * - No domain-specific coupling
 *
 * @example
 * ```tsx
 * <EsignConsent
 *   onConsentChange={(consents) => {
 *     setFormData({ ...formData, consents });
 *   }}
 *   consentItems={[
 *     { id: 'electronic', text: 'I consent to use electronic signatures', required: true },
 *     { id: 'intent', text: 'I intend to sign this document', required: true }
 *   ]}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertCircle, Info } from 'lucide-react';

export interface ConsentItem {
  id: string;
  text: string;
  required?: boolean;
  defaultChecked?: boolean;
  helpText?: string;
}

export interface ConsentState {
  [key: string]: {
    checked: boolean;
    timestamp: string | null;
  };
}

export interface EsignConsentProps {
  // Configuration
  consentItems?: ConsentItem[];
  title?: string;
  description?: string;
  showComplianceInfo?: boolean;

  // Styling
  className?: string;
  variant?: 'default' | 'compact' | 'detailed';

  // Callbacks
  onConsentChange: (consents: ConsentState, allRequiredChecked: boolean) => void;
  onValidationError?: (errors: string[]) => void;
}

// Default ESIGN Act consent items
const DEFAULT_CONSENT_ITEMS: ConsentItem[] = [
  {
    id: 'electronic_consent',
    text: 'I consent to use electronic signatures and records for this transaction',
    required: true,
    helpText: 'Required under the Electronic Signatures in Global and National Commerce Act (ESIGN)'
  },
  {
    id: 'intent_to_sign',
    text: 'I intend to sign this document electronically',
    required: true,
    helpText: 'Confirms your intent to create a legally binding signature'
  },
  {
    id: 'terms_acceptance',
    text: 'I have reviewed and accept the terms and conditions',
    required: true
  },
  {
    id: 'accuracy_certification',
    text: 'I certify that all information provided is accurate and complete',
    required: true
  }
];

export const EsignConsent: React.FC<EsignConsentProps> = ({
  consentItems = DEFAULT_CONSENT_ITEMS,
  title = 'Electronic Signature Consent',
  description,
  showComplianceInfo = true,
  className,
  variant = 'default',
  onConsentChange,
  onValidationError
}) => {
  const [consentState, setConsentState] = useState<ConsentState>(() => {
    // Initialize consent state
    const initialState: ConsentState = {};
    consentItems.forEach((item) => {
      initialState[item.id] = {
        checked: item.defaultChecked || false,
        timestamp: null
      };
    });
    return initialState;
  });

  // Check if all required consents are checked
  const areAllRequiredChecked = (): boolean => {
    return consentItems
      .filter((item) => item.required)
      .every((item) => consentState[item.id]?.checked);
  };

  // Get validation errors
  const getValidationErrors = (): string[] => {
    const errors: string[] = [];
    consentItems
      .filter((item) => item.required && !consentState[item.id]?.checked)
      .forEach((item) => {
        errors.push(`Please confirm: ${item.text}`);
      });
    return errors;
  };

  // Handle checkbox change
  const handleCheckboxChange = (itemId: string, checked: boolean) => {
    setConsentState((prev) => ({
      ...prev,
      [itemId]: {
        checked,
        timestamp: checked ? new Date().toISOString() : null
      }
    }));
  };

  // Notify parent of consent changes
  useEffect(() => {
    const allRequired = areAllRequiredChecked();
    onConsentChange(consentState, allRequired);

    const errors = getValidationErrors();
    if (errors.length > 0 && onValidationError) {
      onValidationError(errors);
    }
  }, [consentState]);

  // Render based on variant
  if (variant === 'compact') {
    return (
      <div className={`space-y-2 ${className || ''}`}>
        {consentItems.map((item) => (
          <div key={item.id} className="flex items-start space-x-2">
            <Checkbox
              id={item.id}
              checked={consentState[item.id]?.checked || false}
              onCheckedChange={(checked) => handleCheckboxChange(item.id, checked === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor={item.id}
              className="text-sm leading-tight cursor-pointer select-none"
            >
              {item.text}
              {item.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
          </div>
        ))}
      </div>
    );
  }

  // Default and detailed variants
  return (
    <div className={`space-y-4 ${className || ''}`}>
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h3 className="font-medium text-base">{title}</h3>
        </div>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>

      {/* Compliance info (if enabled) */}
      {showComplianceInfo && (
        <Alert className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800 text-sm">
            This electronic signature process complies with the U.S. Electronic Signatures
            in Global and National Commerce Act (ESIGN Act) and the Uniform Electronic
            Transactions Act (UETA).
          </AlertDescription>
        </Alert>
      )}

      {/* Consent items */}
      <div className="space-y-3 bg-gray-50 p-4 rounded-lg border">
        {consentItems.map((item) => (
          <div key={item.id} className="space-y-1">
            <div className="flex items-start space-x-2">
              <Checkbox
                id={item.id}
                checked={consentState[item.id]?.checked || false}
                onCheckedChange={(checked) => handleCheckboxChange(item.id, checked === true)}
                className="mt-0.5"
              />
              <div className="flex-1 space-y-1">
                <Label
                  htmlFor={item.id}
                  className="text-sm leading-tight cursor-pointer select-none block"
                >
                  {item.text}
                  {item.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {variant === 'detailed' && item.helpText && (
                  <p className="text-xs text-muted-foreground">{item.helpText}</p>
                )}
                {variant === 'detailed' && consentState[item.id]?.timestamp && (
                  <p className="text-xs text-green-600">
                    Confirmed at: {new Date(consentState[item.id].timestamp!).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Required fields note */}
      <p className="text-xs text-muted-foreground">
        <span className="text-red-500">*</span> Required fields must be checked to proceed
      </p>

      {/* Validation status */}
      {!areAllRequiredChecked() && (
        <Alert variant="default" className="border-yellow-500 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800 text-sm">
            Please confirm all required consent items to continue with the electronic signature process.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};